import React, { useRef, useState } from 'react';
import TitleWithDescription from '../../components/TitleWithDescription/TitleWithDescription';
import BackgroundCover from '../../components/backgroundCover/BackgroundCover';
import { motion, AnimatePresence } from 'framer-motion';
import styles from './HomeTemplate.module.css';
import Title from '../../components/Title/Title';
import { isMobile, isMobileOrTablet } from '../../utils/appUtils';
import { useLanguage } from '../../context/LanguageContext';
import { usePageContent } from '../../hooks/usePageContent';
import { HomeTranslations, homeTemplateTranslations } from '../../translations/homeTemplate';
import { useEditMode } from '../../context/EditModeContext';
import EditableText from '../../components/EditableText/EditableText';

function HomeTemplate() {
    const bgImgRef = useRef<HTMLDivElement>(null);
    const [isExpanded, setIsExpanded] = useState(false);
    const { language } = useLanguage();
    const { isEditMode } = useEditMode();
    const { content, loading } = usePageContent<HomeTranslations>('home');

    // Use English translations as fallback content
    const t = content || homeTemplateTranslations.en!;

    // Show loading state if needed
    if (loading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <div className="animate-spin h-12 w-12 border-2 border-gray-400 rounded-full border-t-transparent"></div>
            </div>
        );
    }

    // Custom title components for editable content
    const EditableMainTitle = () => (
        <EditableText
            contentKey="home.mainTitle"
            defaultValue={t.mainTitle}
            as="span"
        />
    );

    const EditablePhilosophyTitle = () => (
        <EditableText
            contentKey="home.philosophyTitle"
            defaultValue={t.philosophyTitle}
            as="span"
        />
    );

    const EditableIndexGlassTitle = () => (
        <EditableText
            contentKey="home.indexGlassTitle"
            defaultValue={t.indexGlassTitle}
            as="span"
        />
    );

    return (
        <div className="w-full">
            {/* Video Background */}
            <BackgroundCover
                bgImgRef={bgImgRef}
                backgroundHeight={isMobileOrTablet() ? 100 : undefined}
                className="mt-20 md:mt-24"
            >
                <div className='mt-[60px] md:mt-[130px]'>
                    <div className="w-full bg-black flex justify-center items-center">
                        <div className="w-full max-w-[100%] lg:max-w-[80%] mx-auto">
                            <video autoPlay loop muted className="w-full h-auto object-contain">
                                <source src="/video/main-intro.mp4" type="video/mp4" />
                                Your browser does not support the video tag.
                            </video>
                        </div>
                    </div>
                </div>
            </BackgroundCover>

            {/* First Title Block */}
            <div className="w-full px-4 md:px-0">
                <TitleWithDescription
                    title={{
                        firstLine: isEditMode ? <EditableMainTitle /> : t.mainTitle,
                    }}
                    description={
                        isEditMode ? (
                            <EditableText
                                contentKey="home.mainDescription"
                                defaultValue={t.mainDescription}
                                as="div"
                                multiline={true}
                            />
                        ) : t.mainDescription
                    }
                    layout="column"
                    className="px-4 md:px-24 lg:px-96 text-left"
                />
            </div>

            {/* Second Title Block */}
            <div className="w-full px-4 md:px-0">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8 md:gap-0 items-start px-4 md:px-24 py-12 md:py-24">
                    <div className="w-full">
                        <Title
                            firstLine={isEditMode ? <EditablePhilosophyTitle /> : t.philosophyTitle}
                            secondLine={isEditMode ? <EditableIndexGlassTitle /> : t.indexGlassTitle}
                            className="mb-8"
                        />
                        <div className={`max-h-[40vh] overflow-y-auto pr-4 ${styles.customScrollbar}`}>
                            <div className="space-y-6">
                                <p className="text-gray-600 leading-relaxed">
                                    {isEditMode ? (
                                        <EditableText
                                            contentKey="home.firstParagraph"
                                            defaultValue={t.firstParagraph}
                                            multiline={true}
                                        />
                                    ) : t.firstParagraph}
                                </p>
                                <AnimatePresence>
                                    {isExpanded && (
                                        <motion.div
                                            initial={{ opacity: 0, height: 0 }}
                                            animate={{ opacity: 1, height: "auto" }}
                                            exit={{ opacity: 0, height: 0 }}
                                            transition={{ duration: 0.3 }}
                                            className="text-gray-600 leading-relaxed"
                                        >
                                            {isEditMode ? (
                                                <EditableText
                                                    contentKey="home.remainingText"
                                                    defaultValue={t.remainingText}
                                                    as="div"
                                                    multiline={true}
                                                />
                                            ) : (
                                                t.remainingText.split('\n\n').map((paragraph: string, index: number) => (
                                                    <p key={index} className="mb-4">{paragraph}</p>
                                                ))
                                            )}
                                        </motion.div>
                                    )}
                                </AnimatePresence>
                            </div>
                        </div>
                        <button
                            onClick={() => setIsExpanded(!isExpanded)}
                            className="text-gray-500 hover:text-gray-700 transition-colors duration-200 underline mt-4"
                        >
                            {isExpanded ? (
                                isEditMode ? (
                                    <EditableText
                                        contentKey="home.readLess"
                                        defaultValue={t.readLess}
                                    />
                                ) : t.readLess
                            ) : (
                                isEditMode ? (
                                    <EditableText
                                        contentKey="home.readMore"
                                        defaultValue={t.readMore}
                                    />
                                ) : t.readMore
                            )}
                        </button>
                    </div>
                    <div className="relative mt-8 md:mt-0">
                        <div className="md:sticky md:top-24">
                            <img
                                src="/images/homepage_sub2.jpg"
                                alt={t.altText}
                                className="w-full md:w-[700px] h-auto"
                            />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}

export default HomeTemplate; 